import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { Button, Input, SwitchToggle } from '../../ui/atoms';
import { createDataset } from '../../services/datasetsApi';
import useSessionContext from '../../hooks/useSessionContext';

const CreateWordPressDataset = ({ onBack }) => {
  const { t } = useTranslation();
  const [datasetName, setDatasetName] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [showSecrets, setShowSecrets] = useState(false);
  const [visibilityTargets, setVisibilityTargets] = useState(['chat', 'articles']);

  const [wpConfig, setWpConfig] = useState({
    siteUrl: '',
    username: '',
    applicationPassword: '',
    postTypes: ['post', 'page'],
    categories: [],
    tags: []
  });

  const navigate = useNavigate();
  const { token, userId, workspaceId: sessionWorkspaceId, refreshContext } = useSessionContext();

  const validateForm = () => {
    const errors = {};

    if (!datasetName) {
      errors.datasetName = t('datasets.create.wordpress.errors.datasetNameRequired', { defaultValue: 'Dataset name is required' });
    }

    if (!wpConfig.siteUrl) {
      errors.wpSiteUrl = t('datasets.create.wordpress.errors.siteUrlRequired', { defaultValue: 'WordPress site URL is required' });
    }
    if (!wpConfig.username) {
      errors.wpUsername = t('datasets.create.wordpress.errors.usernameRequired', { defaultValue: 'Username is required' });
    }
    if (!wpConfig.applicationPassword) {
      errors.wpAppPassword = t('datasets.create.wordpress.errors.appPasswordRequired', { defaultValue: 'Application password is required' });
    }
    if (visibilityTargets.length === 0) {
      errors.visibilityTargets = t('datasets.create.shared.errors.visibilityRequired', { defaultValue: 'Select at least one availability target' });
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const setVisibilityTarget = (target, enabled) => {
    setVisibilityTargets((prev) => {
      const hasTarget = prev.includes(target);
      if (enabled && !hasTarget) return [...prev, target];
      if (!enabled && hasTarget) return prev.filter((item) => item !== target);
      return prev;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // Handle WordPress case
      // Get workspace ID (from localStorage or API)
      let workspaceId = sessionWorkspaceId;
      if (!workspaceId) {
        const refreshedContext = await refreshContext();
        workspaceId = refreshedContext.workspaceId;
      }
      if (!workspaceId) {
        throw new Error(t('datasets.create.shared.errors.workspaceRequired', { defaultValue: 'Unable to determine workspace ID' }));
      }

      const datasetData = {
        name: datasetName,
        status: 'published',
        type: 'wordpress',
        owner: userId,
        wp_config: JSON.stringify(wpConfig),
        dataset_type: 'wordpress',
        linked_workspace: workspaceId,
        data_sets: visibilityTargets
      };

      await createDataset(token, datasetData);

      navigate('/datasets');

    } catch (error) {
      if (window.location.hostname === 'localhost') {
        console.error('Error:', error.response?.data || error);
      }
      setError(`${t('datasets.create.shared.errors.createFailed', { defaultValue: 'Failed to create dataset' })}: ${error.response?.data?.errors?.[0]?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-8 bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
      <div className="px-4 py-8 sm:p-8">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
            <div className="sm:col-span-6">
              <label htmlFor="dataset-name" className="block text-sm font-medium leading-6 text-gray-900">
                Dataset Name
              </label>
              <div className="mt-2">
                <Input
                  type="text"
                  placeholder="i.e. My WordPress Blog Content"
                  id="dataset-name"
                  value={datasetName}
                  onChange={(e) => setDatasetName(e.target.value)}
                  disabled={isLoading}
                  required
                  error={Boolean(formErrors.datasetName)}
                  className="!py-2.5 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              {formErrors.datasetName && <span className="text-red-500 text-sm">{formErrors.datasetName}</span>}
            </div>

            <div className="sm:col-span-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('datasets.create.shared.availableIn', { defaultValue: 'Available in' })}
                </label>
                <div className="mt-3 space-y-3">
                  <div className="flex items-start justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
                    <div className="pr-4">
                      <p className="text-sm font-medium text-gray-900">
                        {t('datasets.create.shared.visibility.chat.title', { defaultValue: 'Chat / Assistant' })}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {t('datasets.create.shared.visibility.chat.description', {
                          defaultValue: 'Enable if you want this dataset to be visible and used in Assistant.'
                        })}
                      </p>
                    </div>
                    <SwitchToggle
                      checked={visibilityTargets.includes('chat')}
                      onChange={(checked) => !isLoading && setVisibilityTarget('chat', checked)}
                      label={t('datasets.create.shared.visibility.chat.toggleAria', { defaultValue: 'Chat visibility' })}
                      className={isLoading ? 'opacity-50 pointer-events-none' : ''}
                    />
                  </div>
                  <div className="flex items-start justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
                    <div className="pr-4">
                      <p className="text-sm font-medium text-gray-900">
                        {t('datasets.create.shared.visibility.articles.title', { defaultValue: 'Articles' })}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {t('datasets.create.shared.visibility.articles.description', {
                          defaultValue: 'Enable if you want this dataset used while generating articles.'
                        })}
                      </p>
                    </div>
                    <SwitchToggle
                      checked={visibilityTargets.includes('articles')}
                      onChange={(checked) => !isLoading && setVisibilityTarget('articles', checked)}
                      label={t('datasets.create.shared.visibility.articles.toggleAria', { defaultValue: 'Articles visibility' })}
                      className={isLoading ? 'opacity-50 pointer-events-none' : ''}
                    />
                  </div>
                </div>
                {formErrors.visibilityTargets && <span className="text-red-500 text-sm">{formErrors.visibilityTargets}</span>}
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">WordPress Configuration</h3>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label htmlFor="wp-site-url" className="block text-sm font-medium text-gray-700">
                      WordPress Site URL
                    </label>
                    <Input
                      type="url"
                      id="wp-site-url"
                      placeholder="https://yoursite.com"
                      value={wpConfig.siteUrl}
                      onChange={(e) => setWpConfig({ ...wpConfig, siteUrl: e.target.value })}
                      disabled={isLoading}
                      error={Boolean(formErrors.wpSiteUrl)}
                      className="mt-1 disabled:bg-gray-100"
                    />
                    {formErrors.wpSiteUrl && <span className="text-red-500 text-sm">{formErrors.wpSiteUrl}</span>}
                  </div>

                  <div>
                    <label htmlFor="wp-username" className="block text-sm font-medium text-gray-700">
                      Username
                    </label>
                    <Input
                      type="text"
                      id="wp-username"
                      placeholder="WordPress username"
                      value={wpConfig.username}
                      onChange={(e) => setWpConfig({ ...wpConfig, username: e.target.value })}
                      disabled={isLoading}
                      error={Boolean(formErrors.wpUsername)}
                      className="mt-1 disabled:bg-gray-100"
                    />
                    {formErrors.wpUsername && <span className="text-red-500 text-sm">{formErrors.wpUsername}</span>}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label htmlFor="wp-app-password" className="block text-sm font-medium text-gray-700">
                        Application Password
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowSecrets(!showSecrets)}
                        className="text-xs text-primary-600 hover:text-primary-500 flex items-center"
                      >
                        {showSecrets ? <EyeSlashIcon className="h-3 w-3 mr-1" /> : <EyeIcon className="h-3 w-3 mr-1" />}
                        {showSecrets ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    <Input
                      type={showSecrets ? 'text' : 'password'}
                      id="wp-app-password"
                      placeholder="Application password"
                      value={wpConfig.applicationPassword}
                      onChange={(e) => setWpConfig({ ...wpConfig, applicationPassword: e.target.value })}
                      disabled={isLoading}
                      error={Boolean(formErrors.wpAppPassword)}
                      className="mt-1 disabled:bg-gray-100"
                    />
                    {formErrors.wpAppPassword && <span className="text-red-500 text-sm">{formErrors.wpAppPassword}</span>}
                    <p className="mt-1 text-sm text-gray-500">
                      You can create application passwords in your WordPress admin: Users → Profile → Application Passwords
                    </p>
                  </div>
                </div>

                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Post Types to Import
                  </label>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    {['post', 'page', 'product', 'event', 'portfolio', 'testimonial'].map((postType) => (
                      <label key={postType} className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={wpConfig.postTypes.includes(postType)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setWpConfig({ ...wpConfig, postTypes: [...wpConfig.postTypes, postType] });
                            } else {
                              setWpConfig({ ...wpConfig, postTypes: wpConfig.postTypes.filter(pt => pt !== postType) });
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 capitalize">{postType}</span>
                      </label>
                    ))}
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    Select which post types you want to import from your WordPress site
                  </p>
                </div>

                <div className="mt-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">How to Set Up WordPress Connection</h4>
                    <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                      <li>Log into your WordPress admin dashboard</li>
                      <li>Go to Users → Your Profile</li>
                      <li>Scroll down to "Application Passwords" section</li>
                      <li>Enter a name like "AI Writer Integration"</li>
                      <li>Click "Add New Application Password"</li>
                      <li>Copy the generated password and paste it above</li>
                    </ol>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-yellow-900 mb-2">Important Notes</h4>
                    <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
                      <li>Your WordPress site must have the REST API enabled (enabled by default in WordPress 4.7+)</li>
                      <li>Application passwords require WordPress 5.6+ or a plugin like "Application Passwords"</li>
                      <li>We will import post content, titles, excerpts, and metadata</li>
                      <li>Images and media files will not be imported, only text content</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {error && <div className="text-red-500 text-sm mt-4">{error}</div>}
          {isLoading && <div className="mt-4 text-sm text-gray-500">Creating dataset...</div>}

          <div className="mt-6 flex items-center justify-end gap-x-6">
            <Button
              type="button"
              onClick={onBack}
              variant="secondary"
              size="md"
            >
              <ChevronLeftIcon className="h-4 w-4 mr-1" />
              Back
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              variant="primary"
              size="md"
            >
              {isLoading ? 'Creating...' : 'Create WordPress Dataset'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateWordPressDataset; 